import React, { useMemo, useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, fmtDate, useAdminGet } from '../kit';
import { num } from './StepTimings';

/**
 * The full technology card, admin style, for a result the Query explorer
 * lists. Reads search/explorer_tech, which returns db_ops.technology_by_id
 * (the same dict the product's technology page renders) plus `appearances`:
 * how often this id came back in logged queries over the last 90 days and
 * the most recent ten, each a jump back into that query.
 *
 * technology_by_id is a large dict and its keys have drifted over time, so the
 * known ones are laid out by hand and everything is also available under a
 * collapsed "All fields" view — a key this file has not heard of is still
 * visible rather than silently dropped.
 */

const KNOWN = new Set([
  'utech_technology_id', 'title', 'utech_title', 'trl', 'trl_reasoning', 'development_stage',
  'university_name', 'university_country', 'abstract', 'utech_summary', 'summary', 'keywords',
  'utech_keywords', 'industry_categories', 'ip_information', 'ip_summary', 'publications_summary',
  'full_text', 'researchers', 'researcher_ids', 'tech_manager_ids', 'tech_manager_details',
  'tech_manager_default', 'university_portal_url', 'university_technology_url', 'reference_number',
  'common_email', 'tto_office_details',
]);

const jsonText = (v: any): string => {
  try { return JSON.stringify(v ?? null, null, 2); } catch { return String(v); }
};
const isEmptyish = (v: any) =>
  v === null || v === undefined || v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);
const asList = (v: any): any[] => Array.isArray(v) ? v : isEmptyish(v) ? [] : [v];

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex gap-2 text-sm">
    <span className="text-grayscale-1100 w-36 shrink-0">{label}</span>
    <span className="text-grayscale-1300 flex-1 min-w-0 break-words">
      {value === null || value === undefined || value === '' ? '—' : value}
    </span>
  </div>
);

const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[11px] rounded-full border border-grayscale-300 bg-grayscale-200 text-grayscale-1200 px-2 py-0.5 whitespace-nowrap">
    {children}
  </span>
);

const Ext: React.FC<{ href: any; children?: React.ReactNode }> = ({ href, children }) =>
  href ? (
    <a href={String(href)} target="_blank" rel="noreferrer"
      className="text-sm text-primary-700 underline underline-offset-2 break-all">
      {children ?? String(href)}
    </a>
  ) : null;

const Collapse: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="text-xs text-grayscale-1200 underline underline-offset-2 text-left w-fit">
        {open ? '▾' : '▸'} {title}
      </button>
      {open && children}
    </div>
  );
};

/** A contact of any of the shapes the corpus carries ({name,email,url} for a
 *  researcher, {name,email,url,contact_number} for a tech manager,
 *  {name,emails[],source} for the curated TTO office). */
const Contact: React.FC<{ c: any; label?: string }> = ({ c, label }) => {
  if (!c || typeof c !== 'object') return c ? <div className="text-sm text-grayscale-1300">{String(c)}</div> : null;
  const emails = asList(c.emails ?? c.email).filter(Boolean);
  return (
    <div className="text-sm rounded-lg border border-grayscale-300 px-3 py-2 flex flex-col gap-0.5">
      <div className="text-grayscale-1300 font-medium">
        {c.name || c.researcher_name || '(unnamed)'}
        {label && <span className="ml-2 text-[11px] text-grayscale-1100 font-normal">{label}</span>}
      </div>
      {emails.map((e: any) => <div key={String(e)} className="text-xs text-grayscale-1200 break-all">{String(e)}</div>)}
      {c.contact_number && <div className="text-xs text-grayscale-1200">{String(c.contact_number)}</div>}
      {c.url && <Ext href={c.url} />}
      {c.source && <div className="text-[11px] text-grayscale-1100">{String(c.source)}{c.updated_at ? ` · ${fmtDate(c.updated_at)}` : ''}</div>}
    </div>
  );
};

// full_text is included because many patent records carry nothing else but
// a country — without it the row would be a line of dashes.
const PATENT_COLS = ['patent_number', 'patent_type', 'country_of_filing', 'application_date', 'issue_date', 'expiry_date', 'full_text', 'url'];

const TechCardModal: React.FC<{ id: string; onClose: () => void; onOpenQuery?: (queryId: string) => void }> =
  ({ id, onClose, onOpenQuery }) => {
  // The contract names the input utech_technology_id; the sibling handlers
  // take `id`. Both spellings travel so either reading works.
  const d = useAdminGet('search', 'explorer_tech', { id, utech_technology_id: id }, [id]);
  const [copied, setCopied] = useState(false);

  // {utech_technology_id, in_corpus, card: technology_by_id's dict | null,
  //  appearances: {window_days, n, recent: [{id, created_at, query, backend,
  //  rank, percentage, score_rule, hidden_by_threshold}]}}
  const raw = d.data;
  const tech: any = raw?.card && typeof raw.card === 'object' ? raw.card : null;
  const app = raw?.appearances || {};
  const recent: any[] = Array.isArray(app.recent) ? app.recent : [];
  const appN = num(app.n);
  const windowDays = num(app.window_days) ?? 90;

  const found = !!tech && !!tech.utech_technology_id;
  const cats = tech?.industry_categories;
  const major = asList(cats?.major_category ?? cats?.major_categories ?? tech?.major_industry_category);
  const subs = asList(cats?.subcategories ?? cats?.subcategory);
  const keywords = asList(tech?.keywords ?? tech?.utech_keywords);
  const ip = tech?.ip_information && typeof tech.ip_information === 'object' ? tech.ip_information : {};
  const patents = asList(ip.patents);
  const papers = asList(ip.research_papers);
  const researchers = asList(tech?.researchers);
  const managers = asList(tech?.tech_manager_details);
  const publicUrl = tech?.university_technology_url || tech?.university_portal_url || '';
  const rest = useMemo(() => {
    if (!found) return {};
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(tech)) if (!KNOWN.has(k)) out[k] = v;
    return out;
  }, [tech, found]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(jsonText(raw));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Copy this:', jsonText(raw));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-grayscale-300 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-base font-bold text-grayscale-1300 font-grotesk break-words">
              {found ? (tech.title || tech.utech_title || id) : id}
            </div>
            <div className="text-xs text-grayscale-1100 flex items-center gap-2 flex-wrap">
              <span className="font-mono">{id}</span>
              {found && tech.university_name && <><span>·</span><span>{tech.university_name}</span></>}
              {found && tech.university_country && <><span>·</span><span>{tech.university_country}</span></>}
              {found && tech.trl && <Tag>{String(tech.trl)}</Tag>}
              {found && tech.reference_number && <Tag>ref {String(tech.reference_number)}</Tag>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {publicUrl && (
              <a href={String(publicUrl)} target="_blank" rel="noreferrer"
                className="py-1 px-2.5 text-xs rounded-full font-grotesk font-medium whitespace-nowrap border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200">
                Open university page ↗
              </a>
            )}
            {raw && <Btn small onClick={copy}>{copied ? 'Copied' : 'Copy JSON'}</Btn>}
            <Btn small onClick={onClose}>Close</Btn>
          </div>
        </div>
        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {d.error && <Msg kind="error">{d.error}</Msg>}
          {d.loading && !d.data && <Loading />}
          {d.data && !found && (
            <Msg kind="error">No corpus record for {id} — it may have been removed since this search ran.</Msg>
          )}
          {found && (
            <>
              <Section title="Summary">
                <p className="text-sm text-grayscale-1300 whitespace-pre-wrap">
                  {tech.summary || tech.utech_summary || tech.abstract || '—'}
                </p>
              </Section>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Readiness">
                  <Field label="TRL" value={tech.trl} />
                  <p className="text-sm text-grayscale-1300">{tech.trl_reasoning || 'No reasoning recorded.'}</p>
                  {!isEmptyish(tech.development_stage) && typeof tech.development_stage === 'object' && (
                    <Collapse title="Development stage detail"><pre className="text-xs bg-grayscale-100 border border-grayscale-300 rounded-lg p-2 overflow-auto max-h-56 whitespace-pre-wrap break-all">{jsonText(tech.development_stage)}</pre></Collapse>
                  )}
                </Section>
                <Section title="Keywords & categories">
                  <Field label="Major categories" value={major.length ? <span className="flex gap-1 flex-wrap">{major.map((m: any, i: number) => <Tag key={i}>{String(m)}</Tag>)}</span> : ''} />
                  <Field label="Subcategories" value={subs.length ? <span className="flex gap-1 flex-wrap">{subs.map((m: any, i: number) => <Tag key={i}>{String(m)}</Tag>)}</span> : ''} />
                  <Field label="Keywords" value={keywords.length ? <span className="flex gap-1 flex-wrap">{keywords.map((m: any, i: number) => <Tag key={i}>{String(m)}</Tag>)}</span> : ''} />
                </Section>
              </div>

              <Section title={`IP — ${patents.length} patent${patents.length === 1 ? '' : 's'}, ${papers.length} paper${papers.length === 1 ? '' : 's'}`}>
                <p className="text-sm text-grayscale-1300">{tech.ip_summary || ip.ip_summary || 'No IP summary.'}</p>
                {(tech.publications_summary || ip.publications_summary) && (
                  <p className="text-sm text-grayscale-1300">{tech.publications_summary || ip.publications_summary}</p>
                )}
                {patents.length > 0 && (
                  <DataTable rows={patents.map((p: any) => typeof p === 'object' && p ? p : { patent_number: String(p) })}
                    columns={PATENT_COLS} maxHeight="16rem"
                    labels={{ patent_number: 'Number', patent_type: 'Type', country_of_filing: 'Country', application_date: 'Filed', issue_date: 'Issued', expiry_date: 'Expires', full_text: 'Text', url: 'Link' }}
                    render={{
                      full_text: r => r.full_text
                        ? <span className="text-xs text-grayscale-1200" title={String(r.full_text)}>{String(r.full_text).slice(0, 120)}{String(r.full_text).length > 120 ? '…' : ''}</span>
                        : '—',
                      url: r => r.url ? <Ext href={r.url}>open</Ext> : '—',
                    }} />
                )}
                {papers.length > 0 && (
                  <ul className="text-sm text-grayscale-1300 list-disc pl-5 flex flex-col gap-0.5">
                    {papers.map((p: any, i: number) => (
                      <li key={i}>
                        {typeof p === 'object' && p
                          ? <>{p.title || p.name || jsonText(p)} {p.url && <Ext href={p.url}>↗</Ext>}</>
                          : String(p)}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title={`Researchers (${researchers.length})`}>
                  {researchers.length ? researchers.map((r: any, i: number) => <Contact key={i} c={r} />)
                    : <div className="text-xs text-grayscale-1100">None recorded.</div>}
                </Section>
                <Section title="Contacts">
                  {!isEmptyish(tech.tto_office_details) && <Contact c={tech.tto_office_details} label="curated TTO office" />}
                  {!isEmptyish(tech.tech_manager_default) && <Contact c={tech.tech_manager_default} label="default tech manager" />}
                  {managers.map((m: any, i: number) => <Contact key={i} c={m} label="tech manager" />)}
                  <Field label="Common email" value={tech.common_email} />
                  <Field label="Technology page" value={<Ext href={tech.university_technology_url} />} />
                  <Field label="Portal" value={<Ext href={tech.university_portal_url} />} />
                </Section>
              </div>

              {tech.full_text && (
                <Collapse title={`Crawled full text (${String(tech.full_text).length.toLocaleString()} chars)`}>
                  <div className="text-sm bg-grayscale-100 rounded-lg border border-grayscale-300 p-3 max-h-96 overflow-y-auto whitespace-pre-wrap">
                    {String(tech.full_text)}
                  </div>
                </Collapse>
              )}
              <Collapse title={`All fields (${Object.keys(tech).length} keys${Object.keys(rest).length ? `, ${Object.keys(rest).length} not shown above` : ''})`}>
                <pre className="text-xs bg-grayscale-100 border border-grayscale-300 rounded-lg p-2 overflow-auto max-h-[50vh] whitespace-pre-wrap break-all">
                  {jsonText({ ...tech, full_text: tech.full_text ? `(${String(tech.full_text).length} chars, see above)` : tech.full_text })}
                </pre>
              </Collapse>
            </>
          )}

          {/* Outside the `found` guard on purpose: a technology gone from the
              corpus still answers with its appearances — which queries kept
              returning it is precisely what an admin opens a "gone" result
              to learn. */}
          {d.data && (
            <Section title={`Appearances in logged searches${appN !== null ? ` — ${appN.toLocaleString()} in the last ${windowDays} days` : ''}`}>
              {!recent.length ? (
                <div className="text-xs text-grayscale-1100">No logged query returned this technology in the last {windowDays} days.</div>
              ) : (
                <div className="flex flex-col gap-1">
                  {recent.map((a: any, i: number) => (
                    <button key={i} type="button" disabled={!onOpenQuery || !a.id}
                      title={onOpenQuery && a.id ? 'Open this query' : undefined}
                      onClick={() => onOpenQuery && a.id && onOpenQuery(String(a.id))}
                      className="flex items-center gap-3 text-left text-sm rounded-md px-2 py-1 border border-transparent hover:bg-primary-50 hover:border-primary-300 disabled:hover:bg-transparent disabled:cursor-default">
                      <span className="text-xs text-grayscale-1100 whitespace-nowrap w-32">{fmtDate(a.created_at)}</span>
                      <span className="flex-1 min-w-0 truncate text-grayscale-1300">{a.query || '(empty query)'}</span>
                      <span className="text-xs text-grayscale-1100 whitespace-nowrap">
                        {num(a.rank) !== null ? `#${a.rank}` : ''}
                        {num(a.percentage) !== null ? ` · ${Number(a.percentage).toFixed(1)}% ${a.score_rule || ''}` : ''}
                        {a.hidden_by_threshold ? ' · hidden' : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechCardModal;
